import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Search, Compass, AlertTriangle, TrendingUp, TrendingDown, Check as CheckIcon, ChevronLeft, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../hooks/useSubscription';
import { IOS_COST_CATEGORIES } from '../lib/iosCostCategories';
import { AUSTRALIAN_MARKET_PRICES } from '../lib/iosMarketPrices';

const today = () => new Date().toISOString().slice(0, 10);
const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Two-step "Add expense" modal, Feb 26, 2026 iOS-parity refactor.
 *
 * Step 1: pick a CATEGORY (one of the 28 iOS-spec groups).
 * Step 2: pick a LINE ITEM within that category (e.g. "Soil test")
 *         OR type a custom one. Inline market range hint + description
 *         renders the moment a line item is selected, then the AI
 *         validation overlays on top once an amount is typed.
 *
 * Submits `{ title, amount, categoryId, date, notes, buildType }`.
 *   - `title` IS the line-item name (lands in `cost` column on iOS schema)
 *   - `buildType` is the active project tab (multi-project users)
 *
 * `categories` is `useExpensesStore.allCategories`, the 28-entry
 * iOS-spec list with `items` arrays for the step-2 picker.
 */
const ExpenseFormModal = ({ open, onClose, onSubmit, initial, categories = [], buildType }) => {
  const { profile } = useAuth();
  const { isPremium } = useSubscription();
  const [step, setStep] = useState(initial ? 'detail' : 'category');
  const [amount, setAmount] = useState('');
  const [categoryId, setCategoryId] = useState(null);
  const [lineItem, setLineItem] = useState(''); // the specific item name (= submission title)
  const [customLineItem, setCustomLineItem] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [categoryQuery, setCategoryQuery] = useState('');
  const [date, setDate] = useState(today());
  const [notes, setNotes] = useState('');
  const [error, setError] = useState(null);
  const [validation, setValidation] = useState(null);
  const [validating, setValidating] = useState(false);
  const validateTimer = useRef(null);

  // Resolve the currently-selected category
  const selectedCategory = useMemo(
    () => categories.find((c) => c.id === categoryId) || null,
    [categories, categoryId]
  );

  const filteredCategories = useMemo(() => {
    const q = categoryQuery.trim().toLowerCase();
    if (!q) return categories;
    return categories.filter((c) =>
      c.name.toLowerCase().includes(q)
      || (c.items || []).some((it) => it.toLowerCase().includes(q))
    );
  }, [categories, categoryQuery]);

  useEffect(() => {
    if (!open) return;
    setStep(initial ? 'detail' : 'category');
    setAmount(initial?.amount?.toString() || '');
    setCategoryId(initial?.categoryId || null);
    setLineItem(initial?.title || '');
    setCustomLineItem('');
    setShowCustomInput(false);
    setCategoryQuery('');
    setDate(initial?.date || today());
    setNotes(initial?.notes || '');
    setError(null);
    setValidation(null);
  }, [open, initial]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [open, onClose]);

  // AI price validation, debounced after the user types amount.
  // PREMIUM-ONLY: free users see a teaser CTA instead of the validation.
  useEffect(() => {
    if (!open) return undefined;
    if (validateTimer.current) clearTimeout(validateTimer.current);
    if (!isPremium) {
      // Free user, skip the API call entirely
      setValidation(null);
      setValidating(false);
      return undefined;
    }
    const amt = Number(amount);
    const itemName = (lineItem || customLineItem || '').trim();
    if (!Number.isFinite(amt) || amt < 100 || !selectedCategory || !itemName) {
      setValidation(null);
      setValidating(false);
      return undefined;
    }
    setValidating(true);
    validateTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API_URL}/api/validate-price`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            category: itemName,
            amount: amt,
            projectType: 'residential',
            buildType: buildType || profile?.build_type || null,
            state: profile?.state || 'SA',
          }),
        });
        if (res.ok) setValidation(await res.json());
        else setValidation(null);
      } catch (_e) {
        setValidation(null);
      } finally {
        setValidating(false);
      }
    }, 900);
    return () => { if (validateTimer.current) clearTimeout(validateTimer.current); };
  }, [open, amount, categoryId, lineItem, customLineItem, selectedCategory, buildType, profile?.build_type, profile?.state, isPremium]);

  if (!open) return null;

  // Step 1 → Step 2
  const pickCategory = (cat) => {
    setCategoryId(cat.id);
    setStep('item');
    setCategoryQuery('');
  };

  // Step 2 → final detail step (amount entry uses same modal screen for compactness)
  const pickLineItem = (item) => {
    setLineItem(item);
    setCustomLineItem('');
    setShowCustomInput(false);
    setStep('detail');
  };

  const useCustomLineItem = () => {
    const trimmed = customLineItem.trim();
    if (!trimmed) {
      setError('Please type the item name first.');
      return;
    }
    setLineItem(trimmed);
    setShowCustomInput(false);
    setStep('detail');
  };

  const backStep = () => {
    if (step === 'detail') setStep('item');
    else if (step === 'item') setStep('category');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) { setError('Please enter a valid amount.'); return; }
    if (!categoryId) { setError('Please pick a category.'); return; }
    const title = (lineItem || customLineItem).trim() || selectedCategory?.name || 'Expense';
    onSubmit({
      title,
      amount: amt,
      categoryId,
      date: today(),
      notes: notes.trim(),
      buildType: buildType || null,
    });
    onClose();
  };

  return createPortal(
    <div className="bp-workspace">
      <div className="bp-modal-overlay" onClick={onClose} role="dialog" aria-modal="true" data-testid="expense-form-modal">
      <form className="bp-modal-sheet" onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit} noValidate>
        <div className="bp-modal-grabber" aria-hidden="true" />

        {/* Step header with back button */}
        {step !== 'category' && (
          <button
            type="button"
            onClick={backStep}
            className="bp-modal-back"
            data-testid="expense-form-back"
            aria-label="Back"
          >
            <ChevronLeft size={16} /> Back
          </button>
        )}

        <h2 className="bp-modal-title">
          {step === 'category' && (initial ? 'Edit expense' : 'Add expense')}
          {step === 'item' && (selectedCategory?.name || 'Pick an item')}
          {step === 'detail' && (lineItem || customLineItem || 'Add expense')}
        </h2>
        <p className="bp-modal-sub">
          {step === 'category' && 'Pick a category to start.'}
          {step === 'item' && 'Pick a line item or add your own.'}
          {step === 'detail' && (buildType ? `Saving to ${buildType}` : 'Enter the amount.')}
        </p>

        {/* STEP 1, CATEGORY PICKER */}
        {step === 'category' && (
          <div className="bp-form-row">
            <div className="bp-form-cat-search">
              <Search size={14} className="bp-form-cat-search-icon" aria-hidden="true" />
              <input
                type="text"
                className="bp-form-input bp-form-cat-search-input"
                value={categoryQuery}
                onChange={(e) => setCategoryQuery(e.target.value)}
                placeholder={`Search ${categories.length} categories or line items…`}
                data-testid="expense-form-cat-search"
                autoFocus
              />
            </div>
            <div className="bp-form-cat-list" data-testid="expense-form-cat-list">
              {filteredCategories.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => pickCategory(c)}
                  className="bp-form-cat-opt"
                  data-testid={`expense-form-cat-${c.id}`}
                >
                  <span className="dot" style={{ background: c.color }} />
                  <span className="bp-form-cat-opt-label">
                    {c.name}
                    {c.items?.length > 1 && (
                      <span className="bp-form-cat-opt-count">{c.items.length} items</span>
                    )}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* STEP 2, LINE ITEM PICKER */}
        {step === 'item' && selectedCategory && (
          <div className="bp-form-row">
            <div className="bp-form-cat-list" data-testid="expense-form-item-list">
              {(IOS_COST_CATEGORIES[selectedCategory.name] || selectedCategory.items || []).map((item) => {
                // Typical price range is BuildPilot Intelligence data → premium only.
                // Free users see only the line-item name; premium users see the
                // market range + helper description.
                const hint = isPremium ? AUSTRALIAN_MARKET_PRICES[item] : null;
                return (
                  <button
                    key={item}
                    type="button"
                    onClick={() => pickLineItem(item)}
                    className="bp-form-cat-opt is-item"
                    data-testid={`expense-form-item-${item.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    <span className="bp-form-cat-opt-label">
                      <span className="bp-form-cat-opt-name">{item}</span>
                      {hint && (
                        <span className="bp-form-cat-opt-hint">
                          ${hint.min.toLocaleString()}-${hint.max.toLocaleString()} · {hint.description}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
            {/* Custom item entry */}
            {!showCustomInput ? (
              <button
                type="button"
                className="bp-action"
                onClick={() => setShowCustomInput(true)}
                data-testid="expense-form-custom-trigger"
                style={{ marginTop: 12, width: '100%' }}
              >
                + Custom item
              </button>
            ) : (
              <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
                <input
                  type="text"
                  className="bp-form-input"
                  value={customLineItem}
                  onChange={(e) => setCustomLineItem(e.target.value)}
                  placeholder="e.g. Custom finish, special order"
                  data-testid="expense-form-custom-input"
                  autoFocus
                />
                <button
                  type="button"
                  className="bp-action is-primary"
                  onClick={useCustomLineItem}
                  data-testid="expense-form-custom-submit"
                  style={{ flex: '0 0 auto' }}
                >
                  Use
                </button>
              </div>
            )}
          </div>
        )}

        {/* STEP 3, AMOUNT + DETAILS */}
        {step === 'detail' && (
          <>
            <div className="bp-form-row">
              <span className="bp-form-label">Category</span>
              <div className="bp-form-cat-selected" data-testid="expense-form-cat-selected">
                <span className="dot" style={{ background: selectedCategory?.color }} />
                {selectedCategory?.name} · <strong style={{ marginLeft: 4 }}>{lineItem || customLineItem}</strong>
              </div>
            </div>

            <div className="bp-form-row">
              <label className="bp-form-label" htmlFor="bp-exp-amount">Amount (AUD)</label>
              <input
                id="bp-exp-amount"
                type="number"
                min="0"
                step="0.01"
                className="bp-form-input"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                data-testid="expense-form-amount"
                autoFocus
                required
              />
            </div>

            <div className="bp-form-row">
              <label className="bp-form-label" htmlFor="bp-exp-notes">Notes (optional)</label>
              <input
                id="bp-exp-notes"
                type="text"
                className="bp-form-input"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Anything to remember?"
                data-testid="expense-form-notes"
                maxLength={200}
              />
            </div>

            {/* AI price validation overlay, premium only. Free users
                see a teaser CTA when they've entered enough to validate. */}
            {!isPremium && Number(amount) >= 100 && (lineItem || customLineItem) && (
              <div className="bp-form-validation-locked"
                data-testid="expense-form-validation-locked"
                role="status"
              >
                <Lock size={14} className="bp-form-validation-icon" />
                <div className="bp-form-validation-body">
                  <p className="bp-form-validation-msg">BuildPilot Intelligence is a Premium feature</p>
                  <p className="bp-form-validation-tip">
                    Premium price-checks every expense against the South Australian market and flags anything tracking high or low.{' '}
                    <Link to="/workspace/paywall?from=cost-tracker-validation" data-testid="expense-form-validation-cta">
                      Unlock from $19.99/wk →
                    </Link>
                  </p>
                </div>
              </div>
            )}
            {isPremium && (validating || validation) && (
              <div
                className={`bp-form-validation bp-form-validation-${validation?.status || 'loading'}`}
                data-testid="expense-form-validation"
                role="status"
              >
                {validating && (
                  <>
                    <Compass size={14} className="bp-form-validation-icon" />
                    <span>BuildPilot Intelligence is checking market rates…</span>
                  </>
                )}
                {!validating && validation && (
                  <>
                    {validation.status === 'normal' && <CheckIcon size={14} className="bp-form-validation-icon" />}
                    {validation.status === 'low' && <TrendingDown size={14} className="bp-form-validation-icon" />}
                    {validation.status === 'high' && <TrendingUp size={14} className="bp-form-validation-icon" />}
                    {validation.status === 'very_high' && <AlertTriangle size={14} className="bp-form-validation-icon" />}
                    <div className="bp-form-validation-body">
                      <p className="bp-form-validation-msg">{validation.message}</p>
                      {validation.marketMin > 0 && validation.marketMax > 0 && (
                        <p className="bp-form-validation-range">
                          Typical range: ${Math.round(validation.marketMin).toLocaleString('en-AU')}-${Math.round(validation.marketMax).toLocaleString('en-AU')}
                        </p>
                      )}
                      {validation.suggestion && (
                        <p className="bp-form-validation-tip">{validation.suggestion}</p>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}

        {error && (
          <div className="bp-error" style={{ marginBottom: 14 }} data-testid="expense-form-error">{error}</div>
        )}

        <div className="bp-form-actions">
          <button
            type="button"
            onClick={onClose}
            className="bp-action"
            data-testid="expense-form-cancel"
          >
            Cancel
          </button>
          {step === 'detail' && (
            <button
              type="submit"
              className="bp-action is-primary"
              data-testid="expense-form-submit"
              disabled={!amount || !Number.isFinite(Number(amount)) || Number(amount) <= 0}
            >
              {initial ? 'Save changes' : 'Add expense'}
            </button>
          )}
        </div>
      </form>
    </div>
    </div>,
    document.body
  );
};

export default ExpenseFormModal;
